import {messages} from '@smartmixin/i18n';
import React from 'react';
import {
  DateTimeConfig,
  formatDate,
  formatDuration,
  hourFormatter,
  shift,
} from '../Dates';
import {
  SunInfo,
  SunRegime,
  YearDay,
  findNextTransition,
  getEquinoxes,
  getInfo,
  getLightSeconds,
  getYearLightCurve,
  getYearMilestones,
} from './astronomy';
import {skyAt} from './skyColours';
import type {SunIntl} from './dayView';

/**
 * The sun *detail* view models: one day spelled out in full, and the year
 * around it.
 *
 * The headless-presenter seam of `dayView.ts`, one screen deeper. Mobile ships
 * `screens/common/Sun/SunDetailScreen/` — a Today pane, a Year pane and a year
 * chart — and the webapp ships nothing behind its card (FE #168). This module
 * is the extraction that has to land *before* that page is written, not after
 * it: !482 and !483 built the two webapp cards by hand-transcribing the mobile
 * ones and carried three filed defects across verbatim, which is the whole
 * reason FE #170 exists.
 *
 * The same rules as `dayView.ts` and `nowView.ts`, and for the same reasons:
 *
 * - **No layout.** The year chart's geometry is here because it is arithmetic;
 *   the SVG element tree, the label clamping and every stylesheet stay in each
 *   app.
 * - **No theme colours, in either direction.** `yearChartGeometry` hands back a
 *   `fraction` per gradient stop and each app mixes its own ramp — mobile's
 *   `dayCurveLow/Mid/High` are theme tokens and must not be passed in here.
 *   `skyAt` is the one exception the seam allows, because the twilight palette
 *   is a depiction of the sky rather than a theme surface, and it is already in
 *   core.
 * - **No testIDs and no icon names.** Rows carry a stable `id`; each app maps
 *   it.
 *
 * `DAY_EVENTS`, the strip and the milestone rows are lists rather than named
 * fields because membership *is* the logic: the ladder's order is the order the
 * day happens in, and the milestone rows a location gets depend on the regimes
 * it actually has. "Earliest sunset" is not a fact about Tromso.
 */

const SECONDS_PER_DAY = 86400;

/** Days either side of today offered in the strip. */
const STRIP_RADIUS = 3;

/** How far ahead the year pane's trend chip looks. Three weeks is ~1 h at 49 N. */
const TREND_DAYS = 21;

/* -------------------------------------------------------------------------- */
/*                          One day, event by event                           */
/* -------------------------------------------------------------------------- */

export type SunDayEventId = keyof Pick<
  SunInfo,
  | 'nightEnd'
  | 'nauticalDawn'
  | 'dawn'
  | 'sunrise'
  | 'goldenHourEnd'
  | 'solarNoon'
  | 'goldenHour'
  | 'sunset'
  | 'dusk'
  | 'nauticalDusk'
  | 'night'
>;

export type SunDayGroup = 'morning' | 'midday' | 'evening';

export type SunDayEvent = {
  id: SunDayEventId;
  label: {id: string; defaultMessage: string};
  group: SunDayGroup;
  /**
   * The altitude the twilight palette is sampled at for this row — the sun's
   * real altitude for the twilight definitions, and a representative one for
   * the rest. Solar noon is drawn at 50 deg because that is what midday sky
   * looks like, not because the sun reaches it everywhere.
   */
  skyAltitude: number;
  /** Degrees below the horizon that *define* this event, where one does. */
  degreesBelowHorizon?: number;
};

/**
 * The eleven definitions a sun detail page spells out, in the order the day
 * happens in.
 *
 * Exported as the table rather than only as built rows: the ladder itself — its
 * order, which rows are twilight definitions and at what depth — is the shared
 * fact, and a webapp that wants to render it as a table instead of a list still
 * wants exactly these eleven in exactly this order.
 */
export const DAY_EVENTS: readonly SunDayEvent[] = [
  {
    id: 'nightEnd',
    label: messages.astral.astronomicalDawn,
    group: 'morning',
    skyAltitude: -18,
    degreesBelowHorizon: 18,
  },
  {
    id: 'nauticalDawn',
    label: messages.astral.nauticalDawn,
    group: 'morning',
    skyAltitude: -12,
    degreesBelowHorizon: 12,
  },
  {
    id: 'dawn',
    label: messages.astral.civilDawn,
    group: 'morning',
    skyAltitude: -6,
    degreesBelowHorizon: 6,
  },
  {
    id: 'sunrise',
    label: messages.astral.sunrise,
    group: 'morning',
    skyAltitude: 0,
  },
  {
    id: 'goldenHourEnd',
    label: messages.astral.goldenHourEnds,
    group: 'morning',
    skyAltitude: 8,
  },
  {
    id: 'solarNoon',
    label: messages.astral.solarNoon,
    group: 'midday',
    skyAltitude: 50,
  },
  {
    id: 'goldenHour',
    label: messages.astral.goldenHourBegins,
    group: 'evening',
    skyAltitude: 8,
  },
  {
    id: 'sunset',
    label: messages.astral.sunset,
    group: 'evening',
    skyAltitude: 0,
  },
  {
    id: 'dusk',
    label: messages.astral.civilDusk,
    group: 'evening',
    skyAltitude: -6,
    degreesBelowHorizon: 6,
  },
  {
    id: 'nauticalDusk',
    label: messages.astral.nauticalDusk,
    group: 'evening',
    skyAltitude: -12,
    degreesBelowHorizon: 12,
  },
  {
    id: 'night',
    label: messages.astral.astronomicalDusk,
    group: 'evening',
    skyAltitude: -18,
    degreesBelowHorizon: 18,
  },
];

const GROUP_LABEL: Record<SunDayGroup, {id: string; defaultMessage: string}> = {
  morning: messages.astral.morning,
  midday: messages.astral.midday,
  evening: messages.astral.evening,
};

export type SunDayEventRow = {
  id: SunDayEventId;
  group: SunDayGroup;
  /**
   * The group's heading, on the first row of each group and `null` on the
   * others. Computed here rather than in a render loop because it is a property
   * of the list, and both apps otherwise carry the same `lastGroup` mutable.
   */
  groupLabel: string | null;
  label: string;
  /** "05:04", or `null` when the event does not happen on this day. */
  time: string | null;
  /**
   * "Sun 18 deg below the horizon" for a twilight definition, "Does not occur
   * today" for a row with no time, and `null` for a row that needs neither.
   */
  note: string | null;
  /**
   * The twilight palette at this event's altitude, or `null` when the event
   * does not happen — each app maps that to its own muted token rather than
   * receiving a theme colour from here.
   */
  colour: string | null;
};

export type SunDayEventsParams = {
  /** Already resolved by the caller, which usually memoises it. */
  info: SunInfo;
  timezone: string;
  intl: Pick<SunIntl, 'formatMessage'>;
  dateTimeConfig: DateTimeConfig;
};

export const buildDayEvents = ({
  info,
  timezone,
  intl,
  dateTimeConfig,
}: SunDayEventsParams): SunDayEventRow[] => {
  const {formatMessage} = intl;
  const hourFormat = hourFormatter(dateTimeConfig);
  let lastGroup: SunDayGroup | null = null;

  return DAY_EVENTS.map((event) => {
    const timestamp = info[event.id];
    const groupLabel =
      event.group === lastGroup
        ? null
        : formatMessage(GROUP_LABEL[event.group]);
    lastGroup = event.group;

    return {
      id: event.id,
      group: event.group,
      groupLabel,
      label: formatMessage(event.label),
      time:
        timestamp === null ? null : formatDate(timestamp, timezone, hourFormat),
      note:
        timestamp === null
          ? formatMessage(messages.astral.doesNotOccurToday)
          : event.degreesBelowHorizon === undefined
            ? null
            : formatMessage(messages.astral.sunBelowHorizon, {
                degrees: event.degreesBelowHorizon,
              }),
      colour: timestamp === null ? null : skyAt(event.skyAltitude),
    };
  });
};

/* -------------------------------------------------------------------------- */
/*                             The day strip                                  */
/* -------------------------------------------------------------------------- */

export type SunStripDay = {
  /** Days from today; 0 is today. The strip is keyed on this, never on a
   *  timestamp — see `buildDayStrip`. */
  dayOffset: number;
  /** Local midnight of the day. */
  start: number;
  /** `YYYY-MM-DD` in the location's own zone; a stable key. */
  iso: string;
  /** "Fri". */
  weekday: string;
  /** "28". */
  dayOfMonth: string;
  /** "13h51" — the day's own light, formatted. */
  length: string;
  /** "Fri 28": the pill reads as one node rather than three fragments. */
  accessibleName: string;
};

export type SunDayStripParams = {
  /** Local midnight of today, as `Dates.getScaleStart(now, timezone, DAY)`. */
  today: number;
  latitude: number;
  longitude: number;
  timezone: string;
  /** Days either side of today. */
  radius?: number;
  intl: Pick<SunIntl, 'formatMessage'>;
};

/**
 * The days on offer either side of today, each with its own length.
 *
 * Calendar days, not fixed 86400-second steps. The day that ends DST is 25
 * hours long, so stepping by seconds lands two entries on the same date, makes
 * one day unreachable, and points every later entry at the day after the one it
 * is labelled with.
 *
 * `dayOffset` is what a caller should hold as selection state, never `start`:
 * `today` is recomputed on every render, so a stored timestamp desynchronises
 * the moment local midnight passes — or the moment the station changes, since
 * both dashboards re-render the same component instance with new props rather
 * than remounting it — leaving nothing selected and no label saying which day
 * is on show.
 */
export const buildDayStrip = ({
  today,
  latitude,
  longitude,
  timezone,
  radius = STRIP_RADIUS,
  intl,
}: SunDayStripParams): SunStripDay[] =>
  Array.from({length: radius * 2 + 1}, (_, index) => {
    const dayOffset = index - radius;
    const start = shift(today, timezone, dayOffset, 'days');
    const weekday = formatDate(start, timezone, 'ddd');
    const dayOfMonth = formatDate(start, timezone, 'D');
    return {
      dayOffset,
      start,
      iso: formatDate(start, timezone, 'YYYY-MM-DD'),
      weekday,
      dayOfMonth,
      // `getLightSeconds`, not `getLightSummary`: the summary also computes
      // yesterday to get its delta, which is a week of extra ephemeris for a
      // number no entry shows.
      length: formatDuration(
        getLightSeconds({
          timestamp: start + SECONDS_PER_DAY / 2,
          latitude,
          longitude,
        }),
        intl,
      ),
      accessibleName: `${weekday} ${dayOfMonth}`,
    };
  });

/* -------------------------------------------------------------------------- */
/*                                The year                                    */
/* -------------------------------------------------------------------------- */

export type SunYearChipId = 'period' | 'transition' | 'equinox' | 'trend';

export type SunYearChip = {
  id: SunYearChipId;
  text: string;
  /** The one chip carrying the headline. */
  accent: boolean;
};

export type SunYearMilestoneId =
  | 'longestDay'
  | 'shortestDay'
  | 'earliestSunset'
  | 'latestSunrise'
  | 'latestSunset'
  | 'earliestSunrise'
  | 'midnightSun'
  | 'polarNight';

export type SunYearMilestone = {
  id: SunYearMilestoneId;
  label: string;
  /** "22 Dec · 8h14", or "28 Nov – 14 Jan · 48". */
  value: string;
};

export type SunYearTurningPoint = {
  /** `YYYY-MM-DD`, matching `YearDay.date` so a chart can position it. */
  date: string;
  /** "Equinox" / "Solstice". */
  label: string;
};

export type SunYearView = {
  year: number;
  /** One entry per day of the local year. */
  days: YearDay[];
  /** `YYYY-MM-DD` in the location's own zone. */
  todayIso: string;
  /** Twelve already-localised month names, January first. */
  monthLabels: string[];
  turningPoints: SunYearTurningPoint[];
  /** Today's line, pre-formatted. `null` when today is not in `days`. */
  readout: {title: string; detail: string} | null;
  chips: SunYearChip[];
  milestones: SunYearMilestone[];
  /**
   * The ⓘ line under the milestone rows (FE #134).
   *
   * At a temperate latitude it says why neither clock extreme falls on the
   * solstice — the equation of time — and at a polar one why the list is a
   * different list, with the number of days the sun neither rises nor sets.
   * No "ten days before" in the temperate sentence: that figure is true at
   * 49°N and wrong almost everywhere else, and the rows above carry the real
   * dates anyway. `null` within 10° of the equator, where the clock-extreme
   * rows the temperate sentence explains are not on the card either (see
   * `FLAT_LATITUDE`) — a footnote about rows that are not there would be
   * worse than none.
   */
  footnote: string | null;
  /**
   * "This year at 48.9°N".
   *
   * Two messages rather than one with an interpolated "N"/"S": the hemisphere
   * marker is Latin only in western European locales (ru writes с.ш., ja 北緯
   * *before* the number), and appending a Latin letter to an Arabic string
   * splits the token across bidi embedding levels, rendering "N°48.9" reversed.
   */
  title: string;
};

export type SunYearViewParams = {
  latitude: number;
  longitude: number;
  timezone: string;
  /** The instant that decides which year, and which day, is "today". */
  now: number;
  intl: SunIntl;
  dateTimeConfig: DateTimeConfig;
};

/**
 * "16:55", or "4:55 PM".
 *
 * A `YearClockExtreme` carries a minute of the day and no instant, which is not
 * a reason to skip the user's preference: `hourFormatter` takes a config and
 * nothing else, so the minute only has to be given *an* instant to be printed
 * at. UTC noon of an arbitrary day, the same trick `formatMilestoneDate` uses
 * one function down — the date is discarded and no zone can shift the clock.
 *
 * Printing these as bare `HH:mm` while the readout above them followed the
 * preference put two clock formats on one pane.
 */
const formatClock = (minuteOfDay: number, hourFormat: string) =>
  formatDate(
    Date.UTC(2000, 0, 1, Math.floor(minuteOfDay / 60), minuteOfDay % 60) / 1000,
    'utc',
    hourFormat,
  );

/** "12 Dec". Built from the ISO date at UTC noon, so no zone can shift it. */
const formatMilestoneDate = (date: string) =>
  formatDate(
    Math.floor(new Date(`${date}T12:00:00Z`).getTime() / 1000),
    'utc',
    'D MMM',
  );

/**
 * The year, for one place: the curve, the dates worth arguing about, and where
 * in it today falls.
 *
 * A hook rather than a plain builder, like `useSunDayView` and for a sharper
 * reason: a year is ~1100 suncalc calls, and both callers hand the same
 * component instance a new location without remounting it. The memo is part of
 * the contract, not an optimisation a consumer can be trusted to remember.
 *
 * The curve is computed once and handed to `getYearMilestones` rather than
 * being recomputed inside it: without that the year is walked twice, ~2500
 * suncalc calls instead of ~1100.
 */
export const useSunYearView = ({
  latitude,
  longitude,
  timezone,
  now,
  intl,
  dateTimeConfig,
}: SunYearViewParams): SunYearView => {
  const {formatMessage, formatNumber} = intl;

  const year = Number(formatDate(now, timezone, 'YYYY'));
  const todayIso = formatDate(now, timezone, 'YYYY-MM-DD');

  const {days, milestones} = React.useMemo(() => {
    const curve = getYearLightCurve({year, latitude, longitude, timezone});
    return {
      days: curve,
      milestones: getYearMilestones({
        year,
        latitude,
        longitude,
        timezone,
        curve,
      }),
    };
  }, [year, latitude, longitude, timezone]);

  const equinoxes = React.useMemo(() => getEquinoxes(year), [year]);

  const duration = (seconds: number) => formatDuration(seconds, intl);

  /**
   * The two solstices come from the milestone rows rather than from an almanac:
   * at a temperate latitude they are the same day, and inside the Arctic Circle
   * the "longest day" is the middle of a 68-day plateau, which is the honest
   * place to put the tick.
   */
  const turningPoints: SunYearTurningPoint[] = React.useMemo(
    () => [
      ...equinoxes.map((point) => ({
        date: formatDate(point.timestamp, timezone, 'YYYY-MM-DD'),
        label: formatMessage(messages.astral.equinox),
      })),
      ...[milestones.longestDay, milestones.shortestDay]
        .filter((point): point is NonNullable<typeof point> => point !== null)
        .map((point) => ({
          date: point.date,
          label: formatMessage(messages.astral.solstice),
        })),
    ],
    [equinoxes, milestones, timezone, formatMessage],
  );

  // Mid-month, so no zone shift can roll the sample into a neighbouring one.
  // Memoised with the list above because both are handed straight to a chart:
  // a fresh array every render busts the geometry memo on the other side.
  const monthLabels = React.useMemo(
    () =>
      Array.from({length: 12}, (_, month) =>
        formatDate(Date.UTC(year, month, 15) / 1000, 'utc', 'MMM'),
      ),
    [year],
  );

  // Both `year` and `todayIso` come from the same `now`, so the curve always
  // contains today. The guards below are for the type, not for a state a screen
  // can reach.
  const todayIndex = days.findIndex((day) => day.date === todayIso);
  const today = todayIndex === -1 ? null : days[todayIndex];

  const hourFormat = hourFormatter(dateTimeConfig);

  /**
   * Memoised because both apps hand it straight to a chart: `readout` is an
   * object, and a fresh identity every render defeats the `React.memo` on the
   * component that receives it. It also costs a `getInfo` per render.
   *
   * Keyed on `formatMessage` rather than on `intl`, which react-intl rebuilds
   * on every provider render: `formatDuration` reads nothing else off it, and
   * `formatMessage` is stable per locale.
   */
  const readout = React.useMemo(
    () =>
      today === null
        ? null
        : (() => {
            const info = getInfo({
              timestamp: today.timestamp,
              latitude,
              longitude,
            });
            // The user's 12/24-hour preference, not a hardcoded HH:mm: this is
            // the same sunrise the card one screen up prints, and the two must
            // not disagree about how to write a time.
            const clock = (timestamp: number | null) =>
              timestamp === null
                ? '—'
                : formatDate(timestamp, timezone, hourFormat);
            return {
              title: `${formatMilestoneDate(today.date)} · ${formatDuration(
                today.seconds,
                intl,
              )}`,
              detail: `${clock(info.sunrise)} → ${clock(info.sunset)}`,
            };
          })(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [today, latitude, longitude, timezone, hourFormat, formatMessage],
  );

  /**
   * Two chips: where the year is going, and how fast.
   *
   * Under a polar regime neither is the useful sentence — "the equinox is in 24
   * days" is true at Tromso in June and is not what anyone is looking at — so
   * the period and its transition take the slots instead, matching the card.
   */
  const chips = React.useMemo((): SunYearChip[] => {
    const out: SunYearChip[] = [];
    if (today === null) {
      return out;
    }
    const regime: SunRegime = today.regime;
    const isPolar = regime === 'midnightSun' || regime === 'polarNight';

    if (isPolar) {
      const period =
        regime === 'midnightSun'
          ? milestones.midnightSun
          : milestones.polarNight;
      const label =
        regime === 'midnightSun'
          ? messages.astral.midnightSunPeriod
          : messages.astral.polarNightPeriod;
      if (period !== null) {
        const start = days.findIndex((day) => day.date === period.from);
        // The run wraps the year end during a polar night, so counting forward
        // from `start` can go negative — add the year back when it does.
        const elapsed = ((todayIndex - start + days.length) % days.length) + 1;
        out.push({
          id: 'period',
          accent: true,
          text: `${formatMessage(label)} · ${formatMessage(
            messages.astral.dayOfCycle,
            {day: elapsed, total: period.days},
          )}`,
        });
      }
      const transition = findNextTransition({
        timestamp: today.timestamp,
        latitude,
        longitude,
      });
      if (transition !== null) {
        out.push({
          id: 'transition',
          accent: false,
          text: formatMessage(
            transition.kind === 'firstSunrise'
              ? messages.astral.firstSunriseIn
              : messages.astral.firstSunsetIn,
            {days: transition.days},
          ),
        });
      }
      return out;
    }

    const nextEquinox = equinoxes
      .map((point) => ({
        index: days.findIndex(
          (day) =>
            day.date === formatDate(point.timestamp, timezone, 'YYYY-MM-DD'),
        ),
      }))
      .find(({index}) => index > todayIndex);
    if (nextEquinox !== undefined) {
      out.push({
        id: 'equinox',
        accent: true,
        text: formatMessage(messages.astral.equinoxIn, {
          days: nextEquinox.index - todayIndex,
        }),
      });
    }

    const ahead = days[todayIndex + TREND_DAYS];
    if (ahead !== undefined) {
      const delta = ahead.seconds - today.seconds;
      // The duration leads and the message is a plain label, the same shape as
      // the card's day-length chip: interpolating it would put the number after
      // the words in Japanese and Korean for no gain.
      out.push({
        id: 'trend',
        accent: false,
        // `formatDuration` clamps negatives to zero, so the magnitude is passed
        // and the sign printed separately.
        text: `${delta < 0 ? '−' : '+'}${duration(
          Math.abs(delta),
        )} ${formatMessage(messages.astral.overTheNextThreeWeeks)}`,
      });
    }
    return out;
    // Under a polar regime this reaches `findNextTransition`, which scans up to
    // `MAX_TRANSITION_DAYS` days of ephemeris — ~40 suncalc round-trips at
    // Tromso in June, and not something to redo on a layout pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    days,
    todayIndex,
    today,
    milestones,
    equinoxes,
    timezone,
    latitude,
    longitude,
    formatMessage,
  ]);

  /**
   * The rows this location actually has, in a fixed order.
   *
   * A list built by a `push` that drops nulls, not a fixed eight with holes:
   * "earliest sunset" is not a fact about a place where the sun does not set
   * for two months, and the polar periods take those slots instead.
   */
  const milestoneRows = React.useMemo(() => {
    const rows: SunYearMilestone[] = [];
    const push = (
      id: SunYearMilestoneId,
      label: {id: string; defaultMessage: string},
      value: string | null,
    ) => {
      if (value !== null) {
        rows.push({id, label: formatMessage(label), value});
      }
    };
    const extreme = (point: {date: string; seconds: number} | null) =>
      point === null
        ? null
        : `${formatMilestoneDate(point.date)} · ${formatDuration(
            point.seconds,
            intl,
          )}`;
    const clockExtreme = (point: {date: string; minuteOfDay: number} | null) =>
      point === null
        ? null
        : `${formatMilestoneDate(point.date)} · ${formatClock(
            point.minuteOfDay,
            hourFormat,
          )}`;
    const period = (point: {from: string; to: string; days: number} | null) =>
      point === null
        ? null
        : `${formatMilestoneDate(point.from)} – ${formatMilestoneDate(
            point.to,
          )} · ${point.days}`;

    push(
      'longestDay',
      messages.astral.longestDay,
      extreme(milestones.longestDay),
    );
    push(
      'shortestDay',
      messages.astral.shortestDay,
      extreme(milestones.shortestDay),
    );
    push(
      'earliestSunset',
      messages.astral.earliestSunset,
      clockExtreme(milestones.earliestSunset),
    );
    push(
      'latestSunrise',
      messages.astral.latestSunrise,
      clockExtreme(milestones.latestSunrise),
    );
    push(
      'latestSunset',
      messages.astral.latestSunset,
      clockExtreme(milestones.latestSunset),
    );
    push(
      'earliestSunrise',
      messages.astral.earliestSunrise,
      clockExtreme(milestones.earliestSunrise),
    );
    push(
      'midnightSun',
      messages.astral.midnightSunPeriod,
      period(milestones.midnightSun),
    );
    push(
      'polarNight',
      messages.astral.polarNightPeriod,
      period(milestones.polarNight),
    );
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestones, hourFormat, formatMessage]);

  // Chosen by what the location has, like the rows: a place with either polar
  // period gets the polar sentence, with both periods counted together; a
  // place with the clock extremes gets the temperate one; a flat latitude,
  // which has neither, gets nothing.
  const polarDays =
    (milestones.midnightSun?.days ?? 0) + (milestones.polarNight?.days ?? 0);
  const footnote =
    polarDays > 0
      ? formatMessage(messages.astral.milestoneFootnotePolar, {days: polarDays})
      : milestones.earliestSunset !== null
        ? formatMessage(messages.astral.milestoneFootnoteTemperate)
        : null;

  return {
    year,
    days,
    todayIso,
    monthLabels,
    turningPoints,
    readout,
    chips,
    milestones: milestoneRows,
    footnote,
    // `formatNumber`, never `toFixed`: the latter hardcodes the English decimal
    // separator into all 25 locales, which is FE #154. Both bounds, so 49 does
    // not render as a bare "49" beside a "48,9" from the next station.
    title: formatMessage(
      latitude >= 0
        ? messages.astral.thisYearAtNorth
        : messages.astral.thisYearAtSouth,
      {
        degrees: formatNumber(Math.abs(latitude), {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }),
      },
    ),
  };
};
